import React, { useEffect, useMemo, useState } from "react";
import {
  TrendingUpOutlined,
  PersonOutlined,
  StorefrontOutlined,
  ErrorOutlineOutlined,
  ChevronRightOutlined,
  InfoOutlined,
  Inventory2Outlined,
  CalendarMonthOutlined,
  PublicOutlined,
  DescriptionOutlined,
  ArrowUpwardOutlined,
  ArrowDownwardOutlined,
  UnfoldMoreOutlined,
  DownloadOutlined,
} from "@mui/icons-material";
import { MdOutlineShoppingCart } from "react-icons/md";
import { generarReporteExcelConversion } from "./utils/exportarAExcelConversion";
import { APIURL } from "../../configApi/apiConfig";
import axios from "../../configApi/axiosConfig";
import { SeguimientoModal } from "./SeguimientoModal";

const ESTADOS_SOLICITUD_BADGE = {
  1: { etiqueta: "PRE-APROBADO", clase: "bg-blue-100 text-blue-800 border-blue-200" },
  2: { etiqueta: "APROBADO", clase: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  4: { etiqueta: "RECHAZADO", clase: "bg-rose-100 text-rose-800 border-rose-200" },
  6: { etiqueta: "FACTURADO", clase: "bg-purple-100 text-purple-800 border-purple-200" },
};

const COLUMNAS = [
  { key: "consultaCedulas", label: "Consulta Cédulas", badge: "bg-white text-blue-900 border border-gray-300", headerClass: "bg-white text-blue-900" },
  { key: "preAprobadas", label: "Pre-aprobadas", badge: "bg-white text-blue-900 border border-gray-300", headerClass: "bg-white text-blue-900" },
  { key: "solicitudesGestionadas", label: "Gestionadas", badge: "bg-yellow-300 text-blue-900 border border-yellow-400", headerClass: "bg-yellow-300 text-blue-900" },
  { key: "rechazadas", label: "Rechazadas", badge: "bg-white text-blue-900 border border-gray-300", headerClass: "bg-white text-blue-900" },
  { key: "facturados", label: "Facturados", badge: "bg-green-400 text-blue-900 border border-green-500", headerClass: "bg-green-400 text-blue-900" },
  { key: "conversion", label: "Conversión (Consultas)", esPorcentaje: true, headerClass: "bg-gray-300 text-blue-900" },
  { key: "conversionPreAprobadas", label: "Conversión Pre-aprobadas", esPorcentaje: true, headerClass: "bg-gray-300 text-blue-900" },
];

// Mapeo % → color de rendimiento (0-20 rojo, 20-40 naranja, 40-60 ámbar, 60-80
// azul, 80-100 verde). Una sola fuente de verdad para KPI cards, barra de
// progreso de la tabla y el ranking Top 5.
function obtenerTonoConversion(valor) {
  const numero = Number(valor) || 0;
  if (numero >= 80) return { texto: "text-emerald-700", barra: "bg-emerald-500", pista: "bg-emerald-100", tarjeta: "bg-emerald-50 border-emerald-200" };
  if (numero >= 60) return { texto: "text-blue-700", barra: "bg-blue-500", pista: "bg-blue-100", tarjeta: "bg-blue-50 border-blue-200" };
  if (numero >= 40) return { texto: "text-amber-700", barra: "bg-amber-500", pista: "bg-amber-100", tarjeta: "bg-amber-50 border-amber-200" };
  if (numero >= 20) return { texto: "text-orange-700", barra: "bg-orange-500", pista: "bg-orange-100", tarjeta: "bg-orange-50 border-orange-200" };
  return { texto: "text-rose-700", barra: "bg-rose-500", pista: "bg-rose-100", tarjeta: "bg-rose-50 border-rose-200" };
}

const TONOS_KPI = {
  blue: { fondo: "bg-blue-50 border-blue-200", texto: "text-blue-700" },
  violet: { fondo: "bg-violet-50 border-violet-200", texto: "text-violet-700" },
  orange: { fondo: "bg-orange-50 border-orange-200", texto: "text-orange-700" },
  emerald: { fondo: "bg-emerald-50 border-emerald-200", texto: "text-emerald-700" },
};

function BarraConversion({ valor }) {
  const numero = Number(valor) || 0;
  const tono = obtenerTonoConversion(numero);
  return (
    <div className="flex flex-col items-center gap-1 w-16" title={`${numero}% de conversión`}>
      <span className={`text-xs font-bold tabular-nums ${tono.texto}`}>{numero}%</span>
      <div className={`w-full h-1.5 rounded-full ${tono.pista} overflow-hidden`}>
        <div
          className={`h-full rounded-full ${tono.barra} transition-all duration-500 motion-reduce:transition-none`}
          style={{ width: `${Math.min(numero, 100)}%` }}
        />
      </div>
    </div>
  );
}

// Paleta de acento por perfil. Se definen como clases completas (no interpoladas)
// para que Tailwind pueda detectarlas en el build.
const ACENTOS = {
  blue: {
    chipBg: "bg-blue-100",
    chipBorder: "border-blue-200",
    chipText: "text-blue-700",
    rowBg: "bg-blue-50",
    rowHover: "hover:bg-blue-100/70",
    rowHoverGroup: "group-hover:bg-blue-100/70",
    rail: "border-blue-300",
    railBodega: "border-blue-200",
    ring: "focus-visible:ring-blue-400",
  },
  purple: {
    chipBg: "bg-purple-100",
    chipBorder: "border-purple-200",
    chipText: "text-purple-700",
    rowBg: "bg-purple-50",
    rowHover: "hover:bg-purple-100/70",
    rowHoverGroup: "group-hover:bg-purple-100/70",
    rail: "border-purple-300",
    railBodega: "border-purple-200",
    ring: "focus-visible:ring-purple-400",
  },
};

const PERFIL_PRESENTACION = {
  VENDEDOR: {
    titulo: "Mi Conversión",
    descripcion: "Tu desempeño individual en el periodo seleccionado.",
    Icono: PersonOutlined,
    color: "from-indigo-600 to-indigo-700",
    acento: "blue",
  },
  JEFE_ALMACEN: {
    titulo: "Conversión por Región",
    descripcion: "Desempeño comercial agrupado por región, bodega y vendedor.",
    Icono: StorefrontOutlined,
    color: "from-blue-600 to-blue-700",
    acento: "blue",
  },
  SUPERVISOR: {
    titulo: "Conversión por Región",
    descripcion: "Desempeño comercial de tus bodegas asignadas, agrupado por región y vendedor.",
    Icono: StorefrontOutlined,
    color: "from-purple-600 to-purple-700",
    acento: "purple",
  },
};

function sumarIndicadores(vendedores) {
  const total = (Array.isArray(vendedores) ? vendedores : []).reduce(
    (acumulado, item) => ({
      consultaCedulas: acumulado.consultaCedulas + Number(item?.consultaCedulas || 0),
      preAprobadas: acumulado.preAprobadas + Number(item?.preAprobadas || 0),
      solicitudesGestionadas: acumulado.solicitudesGestionadas + Number(item?.solicitudesGestionadas || 0),
      rechazadas: acumulado.rechazadas + Number(item?.rechazadas || 0),
      facturados: acumulado.facturados + Number(item?.facturados || 0),
    }),
    { consultaCedulas: 0, preAprobadas: 0, solicitudesGestionadas: 0, rechazadas: 0, facturados: 0 }
  );

  const conversion = total.consultaCedulas > 0
    ? Math.round((total.facturados / total.consultaCedulas) * 100 * 100) / 100
    : 0;

  const conversionPreAprobadas = total.preAprobadas > 0
    ? Math.round((total.facturados / total.preAprobadas) * 100 * 100) / 100
    : 0;

  return { ...total, conversion, conversionPreAprobadas };
}

const obtenerArray = (arr) => (Array.isArray(arr) ? arr : []);

function construirVendedores(vendedoresRaw) {
  return obtenerArray(vendedoresRaw).map((vendedor) => ({
    idVendedor: vendedor.idVendedor,
    etiqueta: vendedor.vendedor || `Vendedor ${vendedor.idVendedor}`,
    datos: { ...vendedor, ...sumarIndicadores([vendedor]) },
  }));
}

function construirBodegas(bodegasRaw) {
  return obtenerArray(bodegasRaw).map((bodega) => {
    const vendedores = obtenerArray(bodega.vendedores);
    return {
      idBodega: bodega.idBodega,
      etiqueta: bodega.nombreBodega || `Bodega ${bodega.idBodega}`,
      datos: sumarIndicadores(vendedores),
      vendedores: construirVendedores(vendedores),
    };
  });
}

function construirSupervisores(supervisoresRaw) {
  return obtenerArray(supervisoresRaw).map((sup) => {
    const bodegas = obtenerArray(sup.bodegas);
    const vendedoresSup = bodegas.flatMap((b) => obtenerArray(b.vendedores));

    return {
      idSupervisor: sup.idSupervisor,
      etiqueta: sup.nombreSupervisor || `Supervisor ${sup.idSupervisor}`,
      datos: sumarIndicadores(vendedoresSup),
      bodegas: construirBodegas(bodegas),
    };
  });
}

// JEFE_ALMACEN y SUPERVISOR reciben la misma forma de datos (regiones con sus
// supervisores, bodegas y vendedores). Se agrupa en 4 niveles jerárquicos:
function construirFilas(bloque) {
  const regiones = obtenerArray(bloque?.regiones);

  return regiones.map((region) => {
    const supervisores = obtenerArray(region.supervisores);
    const vendedoresRegion = supervisores.flatMap((sup) =>
      obtenerArray(sup.bodegas).flatMap((bodega) => obtenerArray(bodega.vendedores))
    );

    return {
      idRegion: region.idRegion,
      etiqueta: region.nombreRegion || `Región ${region.idRegion}`,
      datos: sumarIndicadores(vendedoresRegion),
      supervisores: construirSupervisores(supervisores),
    };
  });
}

function ChipIndicador({ columna, datos, resaltado, atenuado }) {
  const valor = datos?.[columna.key];
  const numero = valor ?? 0;

  if (columna.key === "conversion" || columna.key === "conversionPreAprobadas") {
    return <BarraConversion valor={numero} />;
  }

  const consultas = Number(datos?.consultaCedulas || 0);
  const titulo =
    columna.key === "consultaCedulas"
      ? `${numero} cédulas consultadas`
      : consultas > 0
        ? `${numero} — ${Math.round((numero / consultas) * 1000) / 10}% de consultas`
        : `${numero}`;

  return (
    <span
      title={titulo}
      className={`inline-flex items-center justify-center gap-1 min-w-[52px] px-2.5 py-1 rounded-full text-xs tabular-nums ${resaltado ? "font-bold" : "font-semibold"} ${atenuado ? "opacity-90" : ""} ${columna.badge}`}
    >
      {numero}
    </span>
  );
}

const PADDING_FILA = { 0: "py-3.5", 1: "py-3", 2: "py-2.5", 3: "py-2" };
const INDENTACION_FILA = { 0: "", 1: "pl-7", 2: "pl-14", 3: "pl-20" };

function FilaIndicadores({ etiqueta, datos, resaltado, nivel = 0, expandible, expandido, contadorHijos, etiquetaContador, onToggle, acento = "blue", zebra, animada, columnaClicable, columnaClicableActiva, onClickColumnaClicable, esWeb }) {
  const tonos = ACENTOS[acento] || ACENTOS.blue;
  const esGrupo = !resaltado && (nivel === 0 || nivel === 1);

  const manejarTeclado = (evento) => {
    if (!expandible) return;
    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault();
      onToggle();
    }
  };

  let claseFondo;
  if (resaltado) {
    claseFondo = "bg-slate-100";
  } else if (nivel === 3) {
    claseFondo = zebra ? "bg-slate-50 group-hover:bg-slate-100" : "bg-white group-hover:bg-slate-50";
  } else if (expandible) {
    claseFondo = `${tonos.rowBg} ${tonos.rowHoverGroup}`;
  } else {
    claseFondo = "bg-white group-hover:bg-slate-50";
  }

  let claseFila = `group transition-colors motion-reduce:transition-none ${claseFondo}`;
  if (animada) claseFila += " animate-fadeUp motion-reduce:animate-none";
  if (resaltado) {
    claseFila += " border-t-2 border-slate-300";
  } else if (expandible) {
    claseFila += ` ${tonos.rowHover} cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${tonos.ring}`;
  } else if (nivel === 3) {
    claseFila += " hover:bg-slate-100/70";
  } else {
    claseFila += " hover:bg-slate-50";
  }

  const rail = nivel === 3 ? "border-slate-200" : expandible ? (nivel === 0 ? tonos.rail : tonos.railBodega) : "border-transparent";

  return (
    <tr
      className={claseFila}
      onClick={expandible ? onToggle : undefined}
      onKeyDown={expandible ? manejarTeclado : undefined}
      role={expandible ? "button" : undefined}
      tabIndex={expandible ? 0 : undefined}
      aria-expanded={expandible ? expandido : undefined}
      aria-label={expandible ? `${etiqueta}, ${etiquetaContador}. Clic para ${expandido ? "ocultar" : "ver"} el detalle.` : undefined}
    >
      <td
        className={`sticky left-0 z-[2] px-4 ${PADDING_FILA[nivel]} text-sm border-b border-slate-100 border-l-4 ${rail} ${claseFondo} ${resaltado ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}
      >
        {nivel > 0 && (
          <span className="absolute left-2.5 top-0 bottom-1/2 w-px bg-slate-300" aria-hidden="true" />
        )}
        {nivel > 0 && (
          <span className="absolute left-2.5 top-1/2 w-2 h-px bg-slate-300" aria-hidden="true" />
        )}
        <div className={`flex items-center gap-2.5 ${INDENTACION_FILA[nivel]}`}>
          {expandible && (
            <ChevronRightOutlined
              fontSize="small"
              aria-hidden="true"
              className={`shrink-0 text-slate-400 transition-transform duration-200 motion-reduce:transition-none ${expandido ? "rotate-90" : ""}`}
            />
          )}

          {esGrupo && nivel === 0 && (
            <span className={`shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center ${tonos.chipBg} ${tonos.chipBorder} ${tonos.chipText}`}>
              <PublicOutlined sx={{ fontSize: 18 }} aria-hidden="true" />
            </span>
          )}

          {esGrupo && nivel === 1 && (
            <span className={`shrink-0 w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center ${tonos.chipText}`}>
              <PersonOutlined sx={{ fontSize: 16 }} aria-hidden="true" />
            </span>
          )}

          {esGrupo && nivel === 2 && (
            <span className={`shrink-0 w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center ${tonos.chipText}`}>
              <StorefrontOutlined sx={{ fontSize: 16 }} aria-hidden="true" />
            </span>
          )}

          {nivel === 3 && (esWeb ? <MdOutlineShoppingCart size={15} className="shrink-0 text-red-500" aria-hidden="true" /> : <PersonOutlined sx={{ fontSize: 15 }} className="shrink-0 text-slate-400" aria-hidden="true" />)}

          <span className={nivel === 3 ? "text-[13px] text-slate-600" : nivel === 2 ? "text-[13px] text-slate-700 font-semibold" : nivel === 1 ? "text-[13px] text-slate-700 font-semibold" : ""}>{etiqueta}</span>

          {expandible && contadorHijos > 0 && (
            <span className="shrink-0 text-[11px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
              {etiquetaContador}
            </span>
          )}
        </div>
      </td>
      {COLUMNAS.map((columna) => {
        const esClicable = columna.key === columnaClicable && typeof onClickColumnaClicable === "function";
        return (
          <td key={columna.key} className={`px-4 ${PADDING_FILA[nivel]} text-center border-b border-slate-100`}>
            {esClicable ? (
              <button
                type="button"
                onClick={(evento) => {
                  evento.stopPropagation();
                  onClickColumnaClicable();
                }}
                aria-expanded={columnaClicableActiva}
                aria-label={`Ver solicitudes gestionadas de ${etiqueta}`}
                className={`inline-flex items-center gap-0.5 rounded-full ring-1 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${columnaClicableActiva ? "ring-blue-400 shadow-sm" : "ring-blue-200 hover:ring-blue-400 hover:shadow-sm"}`}
              >
                <ChipIndicador columna={columna} datos={datos} resaltado={resaltado} atenuado={nivel > 0} />
                <ChevronRightOutlined
                  sx={{ fontSize: 14 }}
                  aria-hidden="true"
                  className={`text-blue-400 transition-transform duration-200 motion-reduce:transition-none mr-1 ${columnaClicableActiva ? "rotate-90" : ""}`}
                />
              </button>
            ) : (
              <ChipIndicador columna={columna} datos={datos} resaltado={resaltado} atenuado={nivel > 0} />
            )}
          </td>
        );
      })}
    </tr>
  );
}

function FilaSolicitudes({ estado, onAbrirSeguimiento }) {
  return (
    <tr className="animate-fadeUp motion-reduce:animate-none">
      <td colSpan={1 + COLUMNAS.length} className="bg-slate-50/80 border-b border-slate-200 px-4 py-3 pl-20">
        {!estado || estado.cargando ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-blue-500 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            Cargando solicitudes…
          </div>
        ) : estado.error ? (
          <p className="text-xs text-rose-600 font-medium">{estado.error}</p>
        ) : estado.datos.length === 0 ? (
          <p className="text-xs text-slate-500">No hay solicitudes gestionadas en este periodo.</p>
        ) : (
          <div className="space-y-1.5">
            {estado.datos.map((solicitud) => {
              const estadoBadge = ESTADOS_SOLICITUD_BADGE[solicitud.estado] || { etiqueta: "—", clase: "bg-slate-100 text-slate-600 border-slate-200" };
              return (
                <button
                  key={solicitud.idCreSolicitudWeb}
                  type="button"
                  onClick={() => onAbrirSeguimiento?.(solicitud)}
                  className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  <DescriptionOutlined sx={{ fontSize: 16 }} className="text-slate-400 shrink-0" aria-hidden="true" />
                  <span className="text-xs font-semibold text-slate-800 w-28 shrink-0 truncate">{solicitud.cedula}</span>
                  <span className="text-xs text-slate-600 flex-1 truncate">{solicitud.cliente || "—"}</span>
                  <span className="text-[11px] text-slate-500 w-28 shrink-0 truncate">{solicitud.producto || "—"}</span>
                  <span className="text-[11px] text-slate-400 w-20 shrink-0">{solicitud.fecha?.substring?.(0, 10)}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${estadoBadge.clase}`}>{estadoBadge.etiqueta}</span>
                  <ChevronRightOutlined sx={{ fontSize: 16 }} className="text-slate-300 shrink-0" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        )}
      </td>
    </tr>
  );
}

function EstadoVacio({ mensaje }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center bg-white">
      <span className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
        <Inventory2Outlined sx={{ fontSize: 22 }} className="text-slate-400" aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-slate-600">Sin datos para este periodo</p>
      <p className="text-xs text-slate-400 max-w-xs">{mensaje}</p>
    </div>
  );
}

function TablaIndicadores({ titulo, filas, cargando, totalFila, colorTitulo, mensajeVacio, acento, fechaConsultaInicio, fechaConsultaFin, onAbrirSeguimiento, ordenarPor, orden, onCambiarOrden }) {
  const [expandidos, setExpandidos] = useState(() => new Set());
  const [solicitudesPorVendedor, setSolicitudesPorVendedor] = useState({});
  const [exportando, setExportando] = useState(false);

  if (cargando) {
    return <EsqueletoTabla titulo={titulo} colorTitulo={colorTitulo} />;
  }

  const alternarExpandido = (clave) => {
    setExpandidos((previo) => {
      const siguiente = new Set(previo);
      if (siguiente.has(clave)) siguiente.delete(clave);
      else siguiente.add(clave);
      return siguiente;
    });
  };

  const exportarAExcel = async (e) => {
    e.stopPropagation();
    if (!filas || filas.length === 0 || exportando) return;

    setExportando(true);
    try {
      await generarReporteExcelConversion({
        filas,
        totalFila,
        titulo,
        fechaConsultaInicio,
        fechaConsultaFin
      });
    } catch (err) {
      console.error("Error al exportar Excel:", err);
    } finally {
      setExportando(false);
    }
  };

  const alternarSolicitudes = async (claveVendedor, idVendedor) => {
    const yaAbierto = expandidos.has(claveVendedor);
    alternarExpandido(claveVendedor);
    if (yaAbierto || solicitudesPorVendedor[claveVendedor] || !idVendedor) return;

    setSolicitudesPorVendedor((previo) => ({ ...previo, [claveVendedor]: { cargando: true, error: null, datos: null } }));
    try {
      const { data } = await axios.get(
        APIURL.get_solicitudesGestionadasConversionPerfil(idVendedor, fechaConsultaInicio, fechaConsultaFin)
      );
      setSolicitudesPorVendedor((previo) => ({
        ...previo,
        [claveVendedor]: { cargando: false, error: null, datos: Array.isArray(data) ? data : [] },
      }));
    } catch {
      setSolicitudesPorVendedor((previo) => ({
        ...previo,
        [claveVendedor]: { cargando: false, error: "No se pudieron cargar las solicitudes.", datos: [] },
      }));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <button
          onClick={exportarAExcel}
          disabled={exportando}
          title="Descargar reporte Excel"
          className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg shadow-sm text-sm font-semibold transition-colors"
        >
          {exportando ? <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : <DownloadOutlined sx={{ fontSize: 18 }} />}
          {exportando ? "Generando Reporte..." : "Descargar Excel"}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[32rem] scrollbar-fina">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr>
                <th className={`sticky top-0 left-0 z-20 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-white border-b border-slate-200 ${colorTitulo}`}>
                  <div className="flex items-center justify-between">
                    <span>{titulo}</span>
                  </div>
                </th>
                {COLUMNAS.map((columna) => {
                  const esOrdenable = typeof onCambiarOrden === "function";
                  const activa = ordenarPor === columna.key;
                  return (
                    <th
                      key={columna.key}
                      className={`sticky top-0 z-10 px-4 py-3 text-center text-xs font-bold uppercase tracking-wide border-b border-slate-200 ${columna.headerClass || 'bg-slate-50 text-slate-600'}`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {esOrdenable ? (
                          <button
                            type="button"
                            onClick={() => onCambiarOrden(columna.key)}
                            className="inline-flex items-center gap-1 cursor-pointer hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                            aria-label={`Ordenar por ${columna.label}${activa ? (orden === "asc" ? ", ascendente" : ", descendente") : ""}`}
                          >
                            {columna.label}
                            {activa ? (
                              orden === "asc" ? (
                                <ArrowUpwardOutlined sx={{ fontSize: 13 }} aria-hidden="true" />
                              ) : (
                                <ArrowDownwardOutlined sx={{ fontSize: 13 }} aria-hidden="true" />
                              )
                            ) : (
                              <UnfoldMoreOutlined sx={{ fontSize: 13 }} className="opacity-50" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          columna.label
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white">
              {filas.map((region, rIndex) => {
                const claveRegion = `r-${region.idRegion ?? rIndex}`;
                const tieneSupervisores = Array.isArray(region.supervisores) && region.supervisores.length > 0;
                const regionExpandida = tieneSupervisores && expandidos.has(claveRegion);
                const totalSupervisores = region.supervisores?.length ?? 0;

                return (
                  <React.Fragment key={claveRegion}>
                    <FilaIndicadores
                      etiqueta={region.etiqueta}
                      datos={region.datos}
                      nivel={0}
                      expandible={tieneSupervisores}
                      expandido={regionExpandida}
                      contadorHijos={totalSupervisores}
                      etiquetaContador={`${totalSupervisores} ${totalSupervisores === 1 ? "supervisor" : "supervisores"}`}
                      onToggle={() => alternarExpandido(claveRegion)}
                      acento={acento}
                    />
                    {regionExpandida &&
                      region.supervisores.map((sup, sIndex) => {
                        const claveSup = `sup-${sup.idSupervisor ?? `${claveRegion}-${sIndex}`}`;
                        const tieneBodegas = Array.isArray(sup.bodegas) && sup.bodegas.length > 0;
                        const supExpandido = tieneBodegas && expandidos.has(claveSup);
                        const totalBodegas = sup.bodegas?.length ?? 0;

                        return (
                          <React.Fragment key={claveSup}>
                            <FilaIndicadores
                              etiqueta={sup.etiqueta}
                              datos={sup.datos}
                              nivel={1}
                              expandible={tieneBodegas}
                              expandido={supExpandido}
                              contadorHijos={totalBodegas}
                              etiquetaContador={`${totalBodegas} ${totalBodegas === 1 ? "bodega" : "bodegas"}`}
                              onToggle={() => alternarExpandido(claveSup)}
                              acento={acento}
                              animada
                            />
                            {supExpandido &&
                              sup.bodegas.map((bodega, bIndex) => {
                                const claveBodega = `b-${bodega.idBodega ?? `${claveSup}-${bIndex}`}`;
                                const tieneVendedores = Array.isArray(bodega.vendedores) && bodega.vendedores.length > 0;
                                const bodegaExpandida = tieneVendedores && expandidos.has(claveBodega);
                                const totalVendedores = bodega.vendedores?.length ?? 0;

                                return (
                                  <React.Fragment key={claveBodega}>
                                    <FilaIndicadores
                                      etiqueta={bodega.etiqueta}
                                      datos={bodega.datos}
                                      nivel={2}
                                      expandible={tieneVendedores}
                                      expandido={bodegaExpandida}
                                      contadorHijos={totalVendedores}
                                      etiquetaContador={`${totalVendedores} ${totalVendedores === 1 ? "vendedor" : "vendedores"}`}
                                      onToggle={() => alternarExpandido(claveBodega)}
                                      acento={acento}
                                      animada
                                    />
                                    {bodegaExpandida &&
                                      bodega.vendedores.map((vendedor, vIndex) => {
                                        const claveSolicitudes = `s-${vendedor.idVendedor ?? `${claveBodega}-${vIndex}`}`;
                                        const solicitudesAbiertas = expandidos.has(claveSolicitudes);

                                        return (
                                          <React.Fragment key={`${claveBodega}-v-${vIndex}`}>
                                            <FilaIndicadores
                                              etiqueta={vendedor.etiqueta}
                                              datos={vendedor.datos}
                                              nivel={3}
                                              zebra={vIndex % 2 === 1}
                                              animada
                                              columnaClicable="solicitudesGestionadas"
                                              columnaClicableActiva={solicitudesAbiertas}
                                              onClickColumnaClicable={() => alternarSolicitudes(claveSolicitudes, vendedor.idVendedor)}
                                              esWeb={vendedor.datos.esWeb}
                                            />
                                            {solicitudesAbiertas && (
                                              <FilaSolicitudes
                                                estado={solicitudesPorVendedor[claveSolicitudes]}
                                                onAbrirSeguimiento={onAbrirSeguimiento}
                                              />
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                  </React.Fragment>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}
              {totalFila && <FilaIndicadores etiqueta="TOTAL" datos={totalFila} resaltado />}
            </tbody>
          </table>

          {filas.length === 0 && <EstadoVacio mensaje={mensajeVacio} />}
        </div>
      </div>
    </div>
  );
}

function BannerInfo({ children }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-blue-100 bg-blue-50 px-3.5 py-2.5">
      <InfoOutlined sx={{ fontSize: 18 }} className="text-blue-500 shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-xs text-blue-800 leading-relaxed">{children}</p>
    </div>
  );
}

function EsqueletoTabla({ titulo = "Cargando...", colorTitulo = "bg-slate-400" }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-auto max-h-[32rem]">
        <table className="w-full min-w-[760px] border-collapse bg-white">
          <thead>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-white border-b border-slate-200 ${colorTitulo}`}>
                {titulo === "Cargando..." ? <div className="h-4 w-24 bg-white/50 rounded animate-pulse" /> : titulo}
              </th>
              {COLUMNAS.map((columna) => (
                <th
                  key={columna.key}
                  className={`px-4 py-3 text-center text-xs font-bold uppercase tracking-wide border-b border-slate-200 ${columna.headerClass || 'bg-slate-50 text-slate-600'}`}
                >
                  {columna.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[0, 1, 2, 3].map((fila) => (
              <tr key={fila}>
                <td colSpan={1 + COLUMNAS.length} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 animate-pulse shrink-0" />
                    <div className="h-3.5 w-32 rounded bg-slate-100 animate-pulse" />
                    <div className="flex-1" />
                    {[0, 1, 2, 3, 4, 5].map((columna) => (
                      <div key={columna} className="h-6 w-12 rounded-full bg-slate-100 animate-pulse" />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TarjetasKPI({ totales }) {
  if (!totales) return null;
  const consultas = Number(totales.consultaCedulas || 0);
  const pct = (valor) => (consultas > 0 ? `${Math.round((valor / consultas) * 1000) / 10}% de consultas` : "—");
  const tonoConversion = obtenerTonoConversion(totales.conversionPreAprobadas);

  const tarjetas = [
    { etiqueta: "Consultas", valor: consultas, nota: "Cédulas consultadas", color: "blue" },
    { etiqueta: "Pre-Aprobado", valor: totales.preAprobadas, nota: pct(totales.preAprobadas), color: "violet" },
    { etiqueta: "Gestionadas", valor: totales.solicitudesGestionadas, nota: pct(totales.solicitudesGestionadas), color: "orange" },
    { etiqueta: "Facturadas", valor: totales.facturados, nota: pct(totales.facturados), color: "emerald" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {tarjetas.map((tarjeta) => (
        <div key={tarjeta.etiqueta} className={`rounded-xl border p-4 ${TONOS_KPI[tarjeta.color].fondo}`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${TONOS_KPI[tarjeta.color].texto}`}>{tarjeta.etiqueta}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{Number(tarjeta.valor || 0).toLocaleString("es-EC")}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{tarjeta.nota}</p>
        </div>
      ))}
      <div className={`rounded-xl border p-4 ${tonoConversion.tarjeta}`}>
        <p className={`text-[11px] font-semibold uppercase tracking-wide ${tonoConversion.texto}`}>Conversión Pre-Aprobadas</p>
        <p className={`text-2xl font-bold mt-1 tabular-nums ${tonoConversion.texto}`}>{totales.conversionPreAprobadas}%</p>
        <div className={`w-full h-1.5 rounded-full ${tonoConversion.pista} mt-2.5 overflow-hidden`}>
          <div
            className={`h-full rounded-full ${tonoConversion.barra} transition-all duration-500 motion-reduce:transition-none`}
            style={{ width: `${Math.min(totales.conversionPreAprobadas, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function TotalesResumen({ filas }) {
  const totalRegiones = filas.length;
  const totalSupervisores = filas.reduce((acc, region) => acc + (region.supervisores?.length || 0), 0);
  const totalBodegas = filas.reduce(
    (acc, region) => acc + (region.supervisores?.reduce((sub, sup) => sub + (sup.bodegas?.length || 0), 0) || 0),
    0
  );
  const totalVendedores = filas.reduce(
    (acc, region) => acc + (region.supervisores?.reduce((sub, sup) => sub + (sup.bodegas?.reduce((sub2, bodega) => sub2 + (bodega.vendedores?.length || 0), 0) || 0), 0) || 0),
    0
  );

  return (
    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
      <span className="flex items-center gap-1">
        <PublicOutlined sx={{ fontSize: 14 }} aria-hidden="true" /> {totalRegiones} {totalRegiones === 1 ? "región" : "regiones"}
      </span>
      <span className="flex items-center gap-1">
        <PersonOutlined sx={{ fontSize: 14 }} aria-hidden="true" /> {totalSupervisores} {totalSupervisores === 1 ? "supervisor" : "supervisores"}
      </span>
      <span className="flex items-center gap-1">
        <StorefrontOutlined sx={{ fontSize: 14 }} aria-hidden="true" /> {totalBodegas} {totalBodegas === 1 ? "bodega" : "bodegas"}
      </span>
      <span className="flex items-center gap-1">
        <PersonOutlined sx={{ fontSize: 14 }} aria-hidden="true" /> {totalVendedores} {totalVendedores === 1 ? "vendedor" : "vendedores"}
      </span>
    </div>
  );
}

function TopBodegas({ filas }) {
  const ranking = useMemo(() => {
    return filas
      .flatMap((region) =>
        (region.supervisores || []).flatMap((sup) =>
          (sup.bodegas || []).map((bodega) => ({
            nombre: bodega.etiqueta,
            conversion: Number(bodega.datos?.conversion || 0),
            consultas: Number(bodega.datos?.consultaCedulas || 0),
          }))
        )
      )
      .filter((bodega) => bodega.consultas > 0)
      .sort((a, b) => b.conversion - a.conversion)
      .slice(0, 5);
  }, [filas]);

  if (ranking.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <TrendingUpOutlined sx={{ fontSize: 16 }} className="text-amber-500" aria-hidden="true" />
        Top 5 Conversión
      </p>
      <div className="space-y-2.5">
        {ranking.map((bodega, indice) => {
          const tono = obtenerTonoConversion(bodega.conversion);
          const medalla =
            indice === 0
              ? "bg-amber-100 text-amber-700"
              : indice === 1
                ? "bg-slate-200 text-slate-600"
                : indice === 2
                  ? "bg-orange-100 text-orange-700"
                  : "bg-slate-100 text-slate-500";
          return (
            <div key={`${bodega.nombre}-${indice}`} className="flex items-center gap-2.5">
              <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${medalla}`}>{indice + 1}</span>
              <span className="text-xs font-medium text-slate-700 flex-1 truncate">{bodega.nombre}</span>
              <span className={`text-xs font-bold tabular-nums ${tono.texto}`}>{bodega.conversion}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatoRelativo(fecha, ahora) {
  if (!fecha) return null;
  const segundos = Math.max(0, Math.floor((ahora - fecha) / 1000));
  if (segundos < 10) return "justo ahora";
  if (segundos < 60) return `hace ${segundos}s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `hace ${horas} h`;
}

export function EstadisticasConversionPerfil({ fechaInicio, fechaFin, filtroBodega, filtroVendedor }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [ahora, setAhora] = useState(() => new Date());
  const [ordenarPor, setOrdenarPor] = useState(null);
  const [orden, setOrden] = useState("desc");

  const alternarOrden = (columna) => {
    if (ordenarPor === columna) {
      setOrden((previo) => (previo === "desc" ? "asc" : "desc"));
    } else {
      setOrdenarPor(columna);
      setOrden("desc");
    }
  };

  useEffect(() => {
    const intervalo = setInterval(() => setAhora(new Date()), 30000);
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    if (!fechaInicio || !fechaFin) return;

    let cancelado = false;

    const fetchEstadisticas = async () => {
      setCargando(true);
      setError(null);
      try {
        const response = await axios.get(
          APIURL.get_estadisticasConversionPerfil(fechaInicio, fechaFin, ordenarPor, orden)
        );
        if (!cancelado) {
          setData(response.data || null);
          setUltimaActualizacion(new Date());
        }
      } catch (err) {
        if (!cancelado) {
          setError(err?.response?.data?.message || "No se pudieron cargar las estadísticas de conversión.");
          setData(null);
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    };

    fetchEstadisticas();
    return () => {
      cancelado = true;
    };
  }, [fechaInicio, fechaFin, ordenarPor, orden]);

  const datosFiltrados = useMemo(() => {
    if (!data) return null;
    if (data.tipoPerfil === "VENDEDOR") return data;

    const bId = filtroBodega === "todos" || !filtroBodega ? null : String(filtroBodega);
    const vId = filtroVendedor === "todos" || !filtroVendedor ? null : String(filtroVendedor);

    const agregarConversionPreAprobadas = (total) => {
      if (!total) return total;
      const conversionPreAprobadas = total.preAprobadas > 0
        ? Math.round((total.facturados / total.preAprobadas) * 100 * 100) / 100
        : 0;
      return { ...total, conversionPreAprobadas };
    };

    if (!bId && !vId) {
      return {
        ...data,
        acumulado: {
          ...data.acumulado,
          total: agregarConversionPreAprobadas(data.acumulado?.total),
        },
        diario: {
          ...data.diario,
          total: agregarConversionPreAprobadas(data.diario?.total),
        },
      };
    }

    const procesarRegiones = (regiones) => {
      if (!regiones) return [];

      return regiones.map(region => {
        if (!region.supervisores) return null;

        const supervisoresFill = region.supervisores.map(sup => {
          if (!sup.bodegas) return null;

          const bodegasFill = sup.bodegas.filter(b => !bId || String(b.idBodega) === bId).map(b => {
            if (!b.vendedores) return null;

            const vendedoresFill = b.vendedores.filter(v => !vId || String(v.idVendedor) === vId);
            if (vendedoresFill.length === 0) return null;

            return { ...b, vendedores: vendedoresFill };
          }).filter(Boolean);

          if (bodegasFill.length === 0) return null;
          return { ...sup, bodegas: bodegasFill };
        }).filter(Boolean);

        if (supervisoresFill.length === 0) return null;
        return { ...region, supervisores: supervisoresFill };
      }).filter(Boolean);
    };

    const calcularTotalPlano = (regiones) => {
      const vendedoresPlanos = regiones.flatMap((r) => r.supervisores.flatMap((s) => s.bodegas.flatMap((b) => b.vendedores)));
      return sumarIndicadores(vendedoresPlanos);
    };

    const regionesAcumulado = procesarRegiones(data.acumulado.regiones);
    const regionesDiario = procesarRegiones(data.diario.regiones);

    return {
      ...data,
      acumulado: {
        ...data.acumulado,
        regiones: regionesAcumulado,
        total: calcularTotalPlano(regionesAcumulado),
      },
      diario: {
        ...data.diario,
        regiones: regionesDiario,
        total: calcularTotalPlano(regionesDiario),
      }
    };
  }, [data, filtroBodega, filtroVendedor]);

  if (cargando && !data) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 bg-slate-100 animate-pulse motion-reduce:animate-none">
          <div className="h-5 w-56 bg-slate-200 rounded" />
          <div className="h-3 w-72 bg-slate-200 rounded mt-2.5" />
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <EsqueletoTabla />
          <EsqueletoTabla />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-rose-100 p-6 flex items-center gap-2 text-rose-700">
        <ErrorOutlineOutlined fontSize="small" />
        <span className="text-sm font-medium">{error}</span>
      </div>
    );
  }

  if (!datosFiltrados) return null;

  const presentacion = PERFIL_PRESENTACION[datosFiltrados.tipoPerfil] || {
    titulo: "Conversión",
    descripcion: "Resumen de conversión para el periodo seleccionado.",
    Icono: TrendingUpOutlined,
    color: "from-slate-600 to-slate-700",
    acento: "blue",
  };
  const { Icono } = presentacion;
  const totalesKpi = datosFiltrados.tipoPerfil === "VENDEDOR" ? datosFiltrados.acumulado : datosFiltrados.acumulado.total;
  const actualizado = formatoRelativo(ultimaActualizacion, ahora);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <style>{`
        .scrollbar-fina { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
        .scrollbar-fina::-webkit-scrollbar { height: 8px; width: 8px; }
        .scrollbar-fina::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-fina::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 9999px; }
        .scrollbar-fina::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
      `}</style>
      <div className={`bg-gradient-to-r ${presentacion.color} px-4 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4 flex-wrap`}>
        <div className="flex items-start gap-3 min-w-0">
          <span className="shrink-0 w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
            <Icono sx={{ fontSize: 24 }} className="text-white" />
          </span>
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-white leading-tight">{presentacion.titulo}</h3>
            <p className="text-xs text-white/80 mt-0.5">
              {presentacion.descripcion}
              {actualizado && <span className="text-white/60"> · Actualizado {actualizado}</span>}
            </p>
          </div>
        </div>
        <span className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-white/90 bg-white/15 px-3 py-1.5 rounded-full">
          <CalendarMonthOutlined sx={{ fontSize: 15 }} aria-hidden="true" />
          {fechaInicio} → {fechaFin}
        </span>
      </div>

      <div className="p-4 sm:p-6 space-y-5">
        <TarjetasKPI totales={totalesKpi} />

        {datosFiltrados.tipoPerfil === "VENDEDOR" ? (
          <TablaIndicadores
            titulo="Periodo"
            colorTitulo="bg-indigo-700"
            cargando={cargando}
            filas={[
              { etiqueta: datosFiltrados.diario.fecha, datos: datosFiltrados.diario },
              { etiqueta: datosFiltrados.acumulado.label, datos: datosFiltrados.acumulado },
            ]}
            mensajeVacio="No hay datos disponibles para el período seleccionado."
          />
        ) : (
          <>
            <TotalesResumen filas={datosFiltrados.acumulado.regiones} />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-4 items-start">
              <div className="space-y-5 min-w-0">
                <BannerInfo>
                  Haz clic en una región para ver sus bodegas, y en una bodega para ver el detalle por vendedor.
                </BannerInfo>
                <TablaIndicadores
                  titulo={datosFiltrados.diario.fecha}
                  colorTitulo="bg-blue-700"
                  cargando={cargando}
                  filas={construirFilas(datosFiltrados.diario)}
                  totalFila={datosFiltrados.diario.total}
                  mensajeVacio="No hay datos disponibles para esta fecha."
                  acento={presentacion.acento}
                  fechaConsultaInicio={datosFiltrados.diario.fecha}
                  fechaConsultaFin={datosFiltrados.diario.fecha}
                  onAbrirSeguimiento={setSolicitudSeleccionada}
                  ordenarPor={ordenarPor}
                  orden={orden}
                  onCambiarOrden={alternarOrden}
                />
                <TablaIndicadores
                  titulo={datosFiltrados.acumulado.label}
                  colorTitulo="bg-purple-700"
                  cargando={cargando}
                  filas={construirFilas(datosFiltrados.acumulado)}
                  totalFila={datosFiltrados.acumulado.total}
                  mensajeVacio="No hay datos disponibles para el período seleccionado."
                  acento={presentacion.acento}
                  fechaConsultaInicio={fechaInicio}
                  fechaConsultaFin={fechaFin}
                  onAbrirSeguimiento={setSolicitudSeleccionada}
                  ordenarPor={ordenarPor}
                  orden={orden}
                  onCambiarOrden={alternarOrden}
                />
              </div>
              <TopBodegas filas={construirFilas(datosFiltrados.acumulado)} />
            </div>
          </>
        )}

        <p className="flex items-center gap-1.5 text-xs text-slate-500 italic">
          <InfoOutlined sx={{ fontSize: 14 }} className="text-slate-400 shrink-0" aria-hidden="true" />
          Fórmula de conversión: {datosFiltrados.formula}
        </p>
      </div>

      <SeguimientoModal
        open={!!solicitudSeleccionada}
        solicitud={solicitudSeleccionada}
        onClose={() => setSolicitudSeleccionada(null)}
      />
    </div>
  );
}
